import * as React from 'react'

import { ApolloQueryResult, useQuery } from '@apollo/client'
import { AtsContextDocument, AtsContextQuery, Exact } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

export type AtsContextState = {
  companyPageReady: boolean
  canUseAts: boolean
  canManageAtsSettings: boolean
  refetch: (
    variables?: Partial<Exact<{ [key: string]: never }>> | undefined
  ) => Promise<ApolloQueryResult<AtsContextQuery>>
  atsCompanyId: number
  talentPoolByDefault: boolean
  requisitionsEnabled: boolean
  joinEnabled: boolean
  joinDirectApplyEnabled: boolean
  hasPremiumPortals: boolean
  referralsEnabled: boolean
  canManagePurchaseRequests: boolean
}

const AtsContext = React.createContext<AtsContextState | undefined>(undefined)

export const useAtsContext = () => {
  const context = React.useContext(AtsContext)

  if (context === undefined) {
    throw new Error('useAtsContext must be used within a AtsContext')
  }
  return context
}

export const AtsContextProvider = ({ children }: React.PropsWithChildren) => {
  const { company } = useCurrentSession()
  const { data, refetch } = useQuery(AtsContextDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const isCompanyPageInstalled =
    data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_careers_page')
      ?.enabled ?? false

  const hasPremiumPortals =
    data?.features.enabledFeatures.nodes.find(
      (node) => node.name === 'dev_ats_join_premium_portals'
    )?.enabled ?? false

  const isCompanyPageConfigured = !!company.slug && !!company.countryFactorialDomain
  const companyPageReady = isCompanyPageInstalled && isCompanyPageConfigured

  const atsCompany = data?.ats.companiesConnection.nodes[0]
  if (!atsCompany) return null

  const joinEnabled = atsCompany.joinEnabled ?? false
  const canManagePurchaseRequests =
    (hasPremiumPortals && data?.integrationsJoin._policies.ordersManageRequests.can) ?? false

  const value = {
    companyPageReady,
    joinEnabled,
    canManagePurchaseRequests,
    hasPremiumPortals: joinEnabled && hasPremiumPortals,
    canUseAts: data.ats._policies.useAts.can,
    canManageAtsSettings: data.ats._policies.manageAtsSettings.can,
    refetch,
    atsCompanyId: atsCompany.id,
    talentPoolByDefault: atsCompany.talentPoolByDefault,
    requisitionsEnabled: atsCompany.requisitionsEnabled ?? false,
    joinDirectApplyEnabled: atsCompany.joinDirectApplyEnabled ?? false,
    referralsEnabled: atsCompany.referralsEnabled ?? false,
  }

  return <AtsContext.Provider value={value}>{children}</AtsContext.Provider>
}
